import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// Auth helpers
export const auth = {
  // Sign up
  signUp: async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: name,
        },
      },
    });
    return { data, error };
  },

  // Sign in
  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current user
  getUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  // Reset password (send email)
  resetPassword: async (email) => {
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${frontendUrl}/reset-password`,
    });
    return { data, error };
  },

  // Update password
  updatePassword: async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  },

  // Update user metadata
  updateUser: async (updates) => {
    const { data, error } = await supabase.auth.updateUser(updates);
    return { data, error };
  },

  // Get session
  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  // Listen to auth changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// Database helpers
export const db = {
  // Profile operations
  profile: {
    get: async (userId) => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      return { data, error };
    },

    create: async (profileData) => {
      const { data, error } = await supabase
        .from('user_profiles')
        .insert(profileData)
        .select()
        .single();
      return { data, error };
    },

    update: async (userId, updates) => {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single();
      return { data, error };
    },

    updateLastLogin: async (userId) => {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('user_id', userId);
      return { data, error };
    },
  },

  // Progress operations
  progress: {
    getAll: async (userId) => {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId);
      return { data, error };
    },

    getByLevel: async (userId, levelId) => {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('level_id', levelId)
        .maybeSingle();
      return { data, error };
    },

    upsert: async (userId, levelId, progressData) => {
      const { data, error } = await supabase
        .from('user_progress')
        .upsert({
          user_id: userId,
          level_id: levelId,
          ...progressData,
        }, {
          onConflict: 'user_id,level_id',
        })
        .select()
        .single();
      return { data, error };
    },
  },

  // Achievements operations
  achievements: {
    getAll: async (userId) => {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', userId);
      return { data, error };
    },

    unlock: async (userId, achievementId) => {
      const { data, error } = await supabase
        .from('user_achievements')
        .insert({
          user_id: userId,
          achievement_id: achievementId,
        })
        .select()
        .single();
      return { data, error };
    },
  },

  // Concepts operations
  concepts: {
    getAll: async (userId) => {
      const { data, error } = await supabase
        .from('user_concepts_learned')
        .select('*')
        .eq('user_id', userId);
      return { data, error };
    },

    markLearned: async (userId, conceptId) => {
      const { data, error } = await supabase
        .from('user_concepts_learned')
        .insert({
          user_id: userId,
          concept_id: conceptId,
        })
        .select()
        .single();
      return { data, error };
    },
  },
};

// Storage helpers
export const storage = {
  // Upload avatar image
  uploadAvatar: async (userId, file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `avatar.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      // Upload file to Supabase Storage
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true, // Overwrite if exists
        });

      if (error) {
        throw error;
      }

      // Get public URL with cache-busting timestamp
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Add timestamp to URL to bust browser cache
      const timestamp = Date.now();
      const urlWithCacheBust = `${urlData.publicUrl}?v=${timestamp}`;

      return { data: urlWithCacheBust, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get avatar public URL
  getAvatarUrl: (userId, fileExt = 'png') => {
    const filePath = `${userId}/avatar.${fileExt}`;
    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },

  // Delete avatar
  deleteAvatar: async (userId, fileExt = 'png') => {
    try {
      const filePath = `${userId}/avatar.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('avatars')
        .remove([filePath]);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },
};

export default supabase;
